import type {Icon} from '@primer/octicons-react'
import {SelectFilterProvider, BaseUserFilterProvider} from '@github-ui/filter-core/providers'
import {FilterValueType} from '@github-ui/filter-core'
import type {FilterKey, FilterSuggestion, SuppliedFilterProviderOptions} from '@github-ui/filter-core'
import type {UserFilterParams} from '@github-ui/filter-core/providers'
import {
  AgentIcon,
  ArchiveIcon,
  PersonIcon,
  CopilotIcon,
  SortDescIcon,
  CheckCircleIcon,
  ClockIcon,
  DotFillIcon,
  SkipIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  TerminalIcon,
  VscodeIcon,
  QuestionBubbleIcon,
} from '@primer/octicons-react'
import type {ActiveTab} from '../types/filter-types'
import {WorkingStatusIcon} from '../components/StatusIcon'
import {
  FILTER_STATUS_IN_PROGRESS,
  FILTER_STATUS_QUEUED,
  FILTER_STATUS_IDLE,
  FILTER_STATUS_NEEDS_ATTENTION,
  FILTER_STATUS_FAILED,
  FILTER_STATUS_COMPLETED,
  FILTER_STATUS_CANCELLED,
  FILTER_STATUS_TIMED_OUT,
  FILTER_STATUS_MERGED,
  FILTER_STATUS_CLOSED,
  FILTER_STATUS_ARCHIVED,
} from './filter-utils'

export class SessionStatusFilterProvider extends SelectFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const filterKey: FilterKey = {
      displayName: 'Is',
      key: 'is',
      priority: 1,
      icon: AgentIcon,
      description: 'Filter by session status',
    }
    const filterValues: FilterSuggestion[] = [
      {value: 'open', displayName: 'Active', type: FilterValueType.Value, priority: 1, icon: AgentIcon},
      {value: 'closed', displayName: 'Completed', type: FilterValueType.Value, priority: 2, icon: ArchiveIcon},
    ]
    super(filterKey, filterValues, {
      ...options,
      strictMatching: true,
      filterTypes: {
        inclusive: true,
        exclusive: false,
        valueless: false,
        hasValue: false,
        multiValue: false,
        multiKey: false,
      },
    })
  }
}

export class SessionCreatorFilterProvider extends BaseUserFilterProvider {
  #hiddenFromSuggestions: boolean

  constructor(
    filterParams?: UserFilterParams,
    options?: SuppliedFilterProviderOptions & {hiddenFromSuggestions?: boolean},
  ) {
    const filterKey: FilterKey = {
      displayName: 'Author',
      key: 'author',
      priority: 3,
      icon: PersonIcon,
      description: 'Filter by author',
    }
    const {hiddenFromSuggestions: hidden, ...providerOptions} = options ?? {}
    super(filterKey, filterParams, {
      ...providerOptions,
      filterTypes: {
        inclusive: true,
        exclusive: true,
        valueless: false,
        hasValue: false,
        multiValue: true,
        multiKey: true,
      },
    })
    this.#hiddenFromSuggestions = hidden ?? false
  }

  getfilterKeys(): FilterKey[] {
    if (this.#hiddenFromSuggestions) return []
    return [
      {
        key: this.key,
        aliases: this.aliases,
        displayName: this.displayName,
        description: this.description,
        priority: this.priority,
        icon: this.icon,
      },
    ]
  }
}

export class SessionSortFilterProvider extends SelectFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const filterKey: FilterKey = {
      displayName: 'Sort',
      key: 'sort',
      priority: 5,
      icon: SortDescIcon,
      description: 'Sort sessions',
    }
    const filterValues: FilterSuggestion[] = [
      {value: 'newest', displayName: 'Newest first', type: FilterValueType.Value, priority: 1},
      {value: 'oldest', displayName: 'Oldest first', type: FilterValueType.Value, priority: 2},
    ]
    super(filterKey, filterValues, {
      ...options,
      strictMatching: true,
      filterTypes: {
        inclusive: true,
        exclusive: false,
        valueless: false,
        hasValue: false,
        multiValue: false,
        multiKey: false,
      },
    })
  }
}

const OPEN_STATUS_SUGGESTIONS: FilterSuggestion[] = [
  {
    value: FILTER_STATUS_IN_PROGRESS,
    displayName: 'Working',
    type: FilterValueType.Value,
    priority: 1,
    icon: WorkingStatusIcon,
  },
  {
    value: FILTER_STATUS_QUEUED,
    displayName: 'Queued',
    type: FilterValueType.Value,
    priority: 2,
    icon: ClockIcon,
    iconColor: 'var(--fgColor-attention)',
  },
  {value: FILTER_STATUS_IDLE, displayName: 'Idle', type: FilterValueType.Value, priority: 3, icon: DotFillIcon},
  {
    value: FILTER_STATUS_NEEDS_ATTENTION,
    displayName: 'Needs attention',
    type: FilterValueType.Value,
    priority: 4,
    icon: QuestionBubbleIcon,
    iconColor: 'var(--fgColor-attention)',
  },
  {
    value: FILTER_STATUS_FAILED,
    displayName: 'Failed',
    type: FilterValueType.Value,
    priority: 5,
    icon: SkipIcon,
    iconColor: 'var(--fgColor-danger)',
  },
  {
    value: FILTER_STATUS_COMPLETED,
    displayName: 'Done',
    type: FilterValueType.Value,
    priority: 6,
    icon: CheckCircleIcon,
  },
  {
    value: FILTER_STATUS_CANCELLED,
    displayName: 'Cancelled',
    type: FilterValueType.Value,
    priority: 7,
    icon: SkipIcon,
    iconColor: 'var(--fgColor-muted)',
  },
  {
    value: FILTER_STATUS_TIMED_OUT,
    displayName: 'Timed out',
    type: FilterValueType.Value,
    priority: 8,
    icon: SkipIcon,
    iconColor: 'var(--fgColor-danger)',
  },
]

const CLOSED_STATUS_SUGGESTIONS: FilterSuggestion[] = [
  {value: FILTER_STATUS_MERGED, displayName: 'Merged', type: FilterValueType.Value, priority: 1, icon: GitMergeIcon},
  {
    value: FILTER_STATUS_CLOSED,
    displayName: 'Closed',
    type: FilterValueType.Value,
    priority: 2,
    icon: GitPullRequestClosedIcon,
  },
  {value: FILTER_STATUS_ARCHIVED, displayName: 'Archived', type: FilterValueType.Value, priority: 3, icon: ArchiveIcon},
]

export class SessionDetailedStatusFilterProvider extends SelectFilterProvider {
  constructor(activeTab: ActiveTab, options?: SuppliedFilterProviderOptions) {
    const filterKey: FilterKey = {
      displayName: 'Status',
      key: 'status',
      priority: 2,
      icon: AgentIcon,
      description: 'Filter by detailed session status',
    }

    let filterValues: FilterSuggestion[]
    if (activeTab === 'open') {
      filterValues = [...OPEN_STATUS_SUGGESTIONS]
    } else if (activeTab === 'closed') {
      filterValues = [...CLOSED_STATUS_SUGGESTIONS]
    } else {
      filterValues = [...OPEN_STATUS_SUGGESTIONS, ...CLOSED_STATUS_SUGGESTIONS]
    }

    super(filterKey, filterValues, {
      ...options,
      strictMatching: true,
      filterTypes: {
        inclusive: true,
        exclusive: false,
        valueless: false,
        hasValue: false,
        multiValue: true,
        multiKey: true,
      },
    })
  }
}

export interface AgentOption {
  key: string
  label: string
  icon?: Icon
}

export class SessionAgentFilterProvider extends SelectFilterProvider {
  constructor(agents: AgentOption[], options?: SuppliedFilterProviderOptions) {
    const filterKey: FilterKey = {
      displayName: 'Agent',
      key: 'agent',
      priority: 4,
      icon: CopilotIcon,
      description: 'Filter by agent',
    }
    const filterValues: FilterSuggestion[] = agents.map((agent, index) => ({
      value: agent.key,
      displayName: agent.label,
      type: FilterValueType.Value,
      priority: index + 1,
      icon: agent.icon ?? CopilotIcon,
    }))
    super(filterKey, filterValues, {
      ...options,
      strictMatching: false,
      filterTypes: {
        inclusive: true,
        exclusive: false,
        valueless: false,
        hasValue: false,
        multiValue: true,
        multiKey: true,
      },
    })
  }
}

export class SessionTypeFilterProvider extends SelectFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    const filterKey: FilterKey = {
      displayName: 'Type',
      key: 'type',
      priority: 4,
      icon: AgentIcon,
      description: 'Filter by agent type',
    }
    const filterValues: FilterSuggestion[] = [
      {value: 'cloud', displayName: 'Cloud agents', type: FilterValueType.Value, priority: 1, icon: AgentIcon},
      {value: 'cli', displayName: 'CLI', type: FilterValueType.Value, priority: 2, icon: TerminalIcon},
      {value: 'vscode', displayName: 'VS Code', type: FilterValueType.Value, priority: 3, icon: VscodeIcon},
    ]
    super(filterKey, filterValues, {
      ...options,
      strictMatching: true,
      filterTypes: {
        inclusive: true,
        exclusive: false,
        valueless: false,
        hasValue: false,
        multiValue: true,
        multiKey: true,
      },
    })
  }
}
